// utils/ownerFundsCreateIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /pro/owner-funds (Owner Advance / Owner Withdrawal
// posting), used by pages/OwnerFundsEntry.tsx -- the only frontend caller
// of this endpoint. Mirrors utils/depositCreateIdempotency.ts's corrected
// "idle" | "in-flight" | "ambiguous" design exactly -- NOT the older "two
// independent refs" pattern still used by utils/vendorPaymentIdempotency.ts
// / utils/creditCardBillPaymentIdempotency.ts (out of scope for this
// patch).
//
// Like NewDeposit.tsx (and unlike FixedAssetDetail.tsx, which is mounted
// once for the route and reused across different asset_id values),
// OwnerFundsEntry.tsx remounts fresh every time the user navigates to
// /owner-funds/new -- so this module tracks exactly ONE
// OwnerFundsCreateAttempt per draft (rehydrated on mount), with no resource-id
// scoping and no "cross-resource" blocked state.

export type OwnerFundsCreatePayload = {
  date: string;
  bank_account_id: number;
  shareholder_account_id: number;
  amount: string;
  mode: string;
  direction: string;
  shareholder_name: string | null;
  description: string | null;
};

/**
 * Deterministic canonical payload for POST /pro/owner-funds. Callers must
 * pass the SAME object (with idempotency_key added) as the actual request
 * body, never a separately reconstructed copy, so the fingerprint can
 * never drift from what is truly sent.
 *
 * - bank_account_id / shareholder_account_id: canonicalized to numbers so
 *   "12" and 12 never produce different fingerprints.
 * - amount: fixed 2-decimal string, matching
 *   pro_journal_entry_router.q2's quantization and
 *   canonicalDepositCreate's identical `.toFixed(2)` choice -- this app has
 *   no frontend-visible company money-scale setting anywhere.
 * - mode / direction: passed through as-is -- both control which accounts
 *   are debited/credited (see backend/pro_journal_entry_router.py's
 *   _create_owner_funds_impl), so either one changing must be material to
 *   the fingerprint.
 * - shareholder_name / description: both trimmed, empty string
 *   canonicalized to null -- matching the backend's own
 *   `(payload.shareholder_name or "").strip() or None` /
 *   `(payload.description or "").strip() or None`. Both are included
 *   because both feed the persisted JournalEntry.description (see
 *   backend/pro_journal_entry_router.py's _owner_funds_memo) -- either one
 *   changing changes the resulting ledger state.
 */
export function canonicalOwnerFundsCreate(
  date: string,
  bankAccountId: string | number,
  shareholderAccountId: string | number,
  amount: string | number,
  mode: string,
  direction: string,
  shareholderName: string,
  description: string,
): OwnerFundsCreatePayload {
  return {
    date,
    bank_account_id: Number(bankAccountId),
    shareholder_account_id: Number(shareholderAccountId),
    amount: Number(amount).toFixed(2),
    mode,
    direction,
    shareholder_name: shareholderName.trim() || null,
    description: description.trim() || null,
  };
}

export function fingerprintOwnerFundsCreate(
  payload: OwnerFundsCreatePayload,
): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt. The next submission always mints a fresh
 *   key.
 * - "in-flight": a request has been sent and its outcome is not yet
 *   known. Transitions to "idle" (success or definite rejection) or
 *   "ambiguous" once the response (or its absence) is classified.
 * - "ambiguous": the last attempt's outcome could not be determined
 *   (network failure, timeout, unreadable response, HTTP 500, or any
 *   other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate JournalEntry; an edited retry stays blocked until this
 *   attempt is resolved.
 */
export type OwnerFundsCreateAttemptState = "idle" | "in-flight" | "ambiguous";

export type OwnerFundsCreateAttempt = {
  state: OwnerFundsCreateAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: OwnerFundsCreatePayload | null;
};

export const IDLE_OWNER_FUNDS_CREATE_ATTEMPT: OwnerFundsCreateAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

/** Restore an interrupted request as uncertain, never as a still-running POST.
 * Older form-only drafts have no attempt and continue to restore normally. */
export function hydrateOwnerFundsCreateAttempt(raw: unknown): OwnerFundsCreateAttempt {
  if (!raw || typeof raw !== "object") return IDLE_OWNER_FUNDS_CREATE_ATTEMPT;
  const a = raw as Partial<OwnerFundsCreateAttempt>;
  const p = a.submittedPayload;
  if (
    (a.state !== "in-flight" && a.state !== "ambiguous") ||
    typeof a.idempotencyKey !== "string" || !a.idempotencyKey ||
    typeof a.submittedFingerprint !== "string" ||
    !p || typeof p !== "object" ||
    typeof p.date !== "string" ||
    typeof p.bank_account_id !== "number" ||
    typeof p.shareholder_account_id !== "number" ||
    typeof p.amount !== "string" || typeof p.mode !== "string" ||
    typeof p.direction !== "string" ||
    (p.shareholder_name !== null && typeof p.shareholder_name !== "string") ||
    (p.description !== null && typeof p.description !== "string") ||
    fingerprintOwnerFundsCreate(p) !== a.submittedFingerprint
  ) return IDLE_OWNER_FUNDS_CREATE_ATTEMPT;
  return {
    state: "ambiguous",
    idempotencyKey: a.idempotencyKey,
    submittedFingerprint: a.submittedFingerprint,
    submittedPayload: p,
  };
}

export type OwnerFundsCreateSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: OwnerFundsCreateAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state and the canonical payload/fingerprint
 * just built from the form. Never mutates `attempt` -- returns a NEW
 * attempt (state "in-flight") for the caller to store when
 * `kind === "start"`.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME fingerprint -> unchanged retry: reuses the stored key
 *    exactly (never mints a new one for an ambiguous outcome -- see module
 *    docstring).
 *  - ambiguous, DIFFERENT fingerprint -> blocked-mismatch: the prior
 *    attempt under this key may already have committed; an edited
 *    resubmission under the same key must never be sent.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decideOwnerFundsCreateAttempt(
  attempt: OwnerFundsCreateAttempt,
  payload: OwnerFundsCreatePayload,
  fingerprint: string,
  generateKey: () => string,
): OwnerFundsCreateSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears the
 * attempt back to idle. */
export function resolveOwnerFundsCreateSuccess(): OwnerFundsCreateAttempt {
  return IDLE_OWNER_FUNDS_CREATE_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400 raised by _create_owner_funds_impl (invalid
 * bank/shareholder account, wrong account for mode/direction, amount <= 0),
 * 401/403 raised by its auth dependencies before the handler body ever
 * runs, 422 from FastAPI's own request-validation short-circuit, a
 * closed-period 409, or an idempotency-conflict 409. In every one of these
 * cases nothing was persisted under this key (either the handler never ran
 * at all, or _create_owner_funds_impl raised before the claim-insert
 * savepoint could complete -- see
 * backend/services/idempotency.run_idempotent_operation), so the identity
 * is free to reuse. Returns to idle so the next submission mints a fresh
 * key and is treated as a new logical attempt, with the user free to edit
 * any field.
 */
export function resolveOwnerFundsCreateDefiniteRejection(): OwnerFundsCreateAttempt {
  return IDLE_OWNER_FUNDS_CREATE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so an unchanged retry safely replays instead of
 * risking a duplicate JournalEntry; an edited retry stays blocked.
 */
export function resolveOwnerFundsCreateAmbiguousFailure(
  attempt: OwnerFundsCreateAttempt,
): OwnerFundsCreateAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether reopening/restoring the Owner Advance & Withdrawal form should
 * repopulate the previously submitted form values instead of resetting to
 * fresh defaults. True only when there is a live AMBIGUOUS attempt.
 *
 * Restoring matters: resetting to fresh defaults (e.g. today's date) would
 * almost never coincidentally match what was actually submitted, so the
 * very next submission would compute a different fingerprint and get
 * blocked as an "edited retry" -- trapping the user with no visible way to
 * make an unchanged retry that safely reuses the key.
 */
export function shouldRestoreAmbiguousAttempt(
  attempt: OwnerFundsCreateAttempt,
): boolean {
  return attempt.state === "ambiguous";
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type OwnerFundsCreateFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection (assert_year_open) and an
 * idempotency-conflict rejection (IdempotencyRequestConflict) share HTTP
 * 409 but require different messaging and a different interpretation -- a
 * closed-period 409 is detected via `isClosedPeriod`'s message match; any
 * OTHER 409 is treated as an idempotency conflict, mirroring
 * classifyDepositCreateFailure's identical reasoning.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce -- 400
 * raised directly by _create_owner_funds_impl, 401/403 raised by auth
 * dependencies (resolve before the handler body runs at all), and 422 from
 * FastAPI's own RequestValidationError handler (also short-circuits before
 * the handler runs) -- is treated as a definite rejection uniformly: every
 * one of these occurs strictly before (or in place of) any business
 * write, so nothing was ever persisted under the key.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for those
 * -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response.
 */
export function classifyOwnerFundsCreateFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): OwnerFundsCreateFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // Network failure, timeout, or any other rejection with no HTTP
    // response at all -- outcome unknown, must be treated as ambiguous.
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    // Every other 4xx (400, 401, 403, 422, and any other well-formed 4xx)
    // -- see the module-level rationale above. Deterministic: nothing was
    // ever persisted under this key.
    return "definite-rejection";
  }

  // 5xx (500 and any other), and anything outside the 4xx/409 ranges this
  // app's own code never emits: ambiguous -- the backend's replay loader
  // (_load_owner_funds_entry) runs AFTER db.commit(), so a 500 can mean the
  // JournalEntry WAS persisted and only response construction or replay
  // validation failed. Fails closed for anything not positively identified
  // as a definite pre-commit rejection.
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt
 * remains unresolved. Deliberately does NOT say reloading alone is safe --
 * a reload proves nothing about whether the prior attempt already posted.
 * Must convey all three ideas: (1) the prior attempt may already have been
 * recorded, (2) restoring the original values permits a safe retry under
 * the same key, (3) the user should check the Owner Funds list before
 * starting a new one.
 */
export const OWNER_FUNDS_CREATE_EDITED_RETRY_BLOCKED_MESSAGE =
  "This entry may already be recorded. Original details restored. Retry, or check the Owner Funds list first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the entry may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const OWNER_FUNDS_CREATE_CONFLICT_MESSAGE =
  "Couldn't confirm this entry. Check the Owner Funds list before trying again.";
